import { Link } from "react-router-dom";
import { ArrowRight, Mail, BookOpen, Calculator, Percent, MapPin, Check } from "lucide-react";
import EmailCapture from "../EmailCapture";
import { SectionHeader, Reveal } from "./primitives";
import { trackCTAClick } from "../../lib/tracking";

export function AlertsSection() {
  return (
    <section className="py-14 lg:py-20 bg-rd-offwhite" data-testid="deal-alerts-section">
      <div className="rd-container">
        <Reveal className="rd-card-dark rd-grain overflow-hidden">
          <div className="relative p-7 sm:p-10 lg:p-12 grid lg:grid-cols-[1fr_1.35fr] gap-8 items-center">
            <div>
              <p className="rd-eyebrow text-rd-action mb-3 inline-flex items-center gap-2"><Mail className="w-4 h-4" aria-hidden="true" /> Free deal alerts</p>
              <h2 className="rd-h2 text-3xl sm:text-4xl text-white">Let the Best Deals Come to You.</h2>
              <p className="mt-3 text-white/70 text-base sm:text-lg">Get high-cash-flow opportunities delivered to your inbox.</p>
            </div>
            <div>
              <EmailCapture variant="panel" />
              <p className="mt-3 text-xs text-white/45">Unsubscribe anytime. No spam.</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function PricingCard({ name, price, period, description, features = [], badge, highlight = false, accent = "neutral", cta, onClick, testId }) {
  const accentText = accent === "green" ? "text-rd-green" : accent === "amber" ? "text-rd-fair" : accent === "navy" ? "text-rd-navy" : "text-rd-text-2";
  return (
    <div className={`relative rd-card flex flex-col p-6 ${highlight ? "border-2 border-rd-green shadow-[0_20px_50px_-30px_rgba(22,163,106,0.5)]" : ""}`} data-testid={`${testId}-card`}>
      {badge && (
        <span className={`absolute -top-3 left-6 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md ${highlight ? "bg-rd-green text-white" : "bg-rd-navy text-white"}`}>{badge}</span>
      )}
      <p className={`rd-eyebrow ${accentText}`}>{name}</p>
      <p className="mt-3 flex items-baseline gap-1">
        <span className="font-mono font-bold text-4xl text-rd-text tabular-nums">{price}</span>
        {period && <span className="text-sm text-rd-text-2">{period}</span>}
      </p>
      <p className="mt-3 text-sm text-rd-text-2 leading-relaxed">{description}</p>
      {features.length > 0 && (
        <ul className="mt-5 mb-6 space-y-2 text-sm text-rd-text">
          {features.map((f) => (
            <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-rd-green mt-0.5 flex-shrink-0" aria-hidden="true" /> {f}</li>
          ))}
        </ul>
      )}
      <button type="button" onClick={onClick} className={`rd-btn w-full mt-auto ${highlight ? "rd-btn-primary" : "rd-btn-secondary-dark"}`} data-testid={testId}>
        {cta}
      </button>
    </div>
  );
}

export function PricingSection({ isAuthenticated, navigate }) {
  const go = (cta, to) => { trackCTAClick(cta, to); navigate(to); };
  return (
    <section className="rd-section bg-white border-y border-rd-border" data-testid="landing-pricing-section" id="pricing">
      <div className="rd-container">
        <Reveal><SectionHeader eyebrow="Pricing" title="Start Free. Upgrade When You Need More." subtitle="Analyze deals for free, unlock one property for $7, or upgrade for advanced investing tools." align="center" /></Reveal>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
          <PricingCard
            name="Free" price="$0" description="Explore deals and run basic analysis."
            features={["Property search & Deal Score", "Basic cash flow analysis", "Top Deals pages"]}
            cta={isAuthenticated ? "Open Dashboard" : "Get Started"}
            onClick={() => go("landing_pricing_free", isAuthenticated ? "/dashboard" : "/auth")}
            testId="landing-pricing-free-cta"
          />
          <PricingCard
            name="Deep-Dive" price="$7" period="/ property" accent="amber" badge="One-time"
            description="Detailed analysis of one property. Includes 7 days of Pro access. No auto-subscription."
            features={["Full detailed report", "7-day Pro access", "One-time payment"]}
            cta="Analyze a Property"
            onClick={() => go("landing_pricing_deep_dive", "/dashboard")}
            testId="landing-pricing-deep-dive-cta"
          />
          <PricingCard
            name="Pro" price="$19" period="/ month" accent="green" badge="Most Popular" highlight
            description="For active investors who want unlimited analysis, AI insights and exports."
            features={["Unlimited searches & analyses", "BRRRR, Flip & Airbnb analysis", "AI deal insights", "PDF / Excel export"]}
            cta={isAuthenticated ? "Upgrade to Pro" : "Get Pro"}
            onClick={() => go("landing_pricing_pro", isAuthenticated ? "/billing?tier=pro_monthly" : "/pricing")}
            testId="landing-pricing-pro-cta"
          />
          <PricingCard
            name="Elite" price="$39" period="/ month" accent="navy"
            description="Manage, optimize and grow a portfolio with advanced tools."
            features={["Everything in Pro", "Portfolio dashboard", "Bulk analysis (100 rows)", "Custom deal scoring"]}
            cta={isAuthenticated ? "Go Elite" : "Get Elite"}
            onClick={() => go("landing_pricing_elite", isAuthenticated ? "/billing?tier=elite_monthly" : "/pricing")}
            testId="landing-pricing-elite-cta"
          />
        </div>
        <div className="mt-8 text-center">
          <button type="button" onClick={() => go("pricing_preview", "/pricing")} className="rd-link inline-flex items-center gap-1.5 min-h-[44px]" data-testid="pricing-preview-link">
            Compare all plan details <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </section>
  );
}

const GUIDES = [
  { to: "/how-to-analyze-rental-properties", icon: BookOpen, title: "How to Analyze Rental Properties", desc: "The step-by-step framework behind every RealixData analysis." },
  { to: "/cash-flow-calculator-real-estate", icon: Calculator, title: "Cash Flow Calculator", desc: "Free tool — run the numbers on any deal manually." },
  { to: "/what-is-a-good-cap-rate", icon: Percent, title: "What Is a Good Cap Rate?", desc: "Benchmarks by market and property type." },
  { to: "/best-rental-markets-tampa-jacksonville-atlanta", icon: MapPin, title: "Best Rental Markets", desc: "Where the numbers work right now." },
];

export function GuidesSection() {
  return (
    <section className="rd-section bg-rd-offwhite" data-testid="guides-section">
      <div className="rd-container">
        <Reveal>
          <SectionHeader
            eyebrow="Resources"
            title="Learn the Numbers"
            action={<Link to="/faq" className="rd-link inline-flex items-center gap-1.5 text-base min-h-[44px]" data-testid="guides-view-all">All FAQ & Guides <ArrowRight className="w-4 h-4" aria-hidden="true" /></Link>}
          />
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {GUIDES.map((g, i) => (
            <Reveal key={g.to} delay={i * 70}>
              <Link to={g.to} className="rd-card rd-card-hover p-6 flex flex-col h-full group" data-testid={`guide-${g.to.slice(1)}`}>
                <span className="w-10 h-10 rounded-lg bg-rd-green-soft text-rd-green inline-flex items-center justify-center"><g.icon className="w-5 h-5" aria-hidden="true" /></span>
                <h3 className="rd-h3 text-lg text-rd-text mt-5">{g.title}</h3>
                <p className="mt-2 text-sm text-rd-text-2 leading-relaxed flex-1">{g.desc}</p>
                <span className="mt-4 rd-link inline-flex items-center gap-1 text-sm">Read guide <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" /></span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FinalCTASection({ onAnalyze, onFindDeals }) {
  return (
    <section className="rd-section bg-rd-navy rd-grain text-white" data-testid="final-cta-section">
      <div className="rd-container relative text-center max-w-3xl">
        <Reveal>
          <h2 className="rd-h2 text-4xl sm:text-5xl lg:text-[56px]">Stop Scrolling Listings.<br />Start Finding Investments.</h2>
          <p className="mt-5 text-lg text-white/70">Run the numbers before you make the offer.</p>
          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button type="button" onClick={onAnalyze} className="rd-btn rd-btn-primary h-[52px] px-7 w-full sm:w-auto" data-testid="bottom-cta">
              Analyze a Property <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </button>
            <button type="button" onClick={onFindDeals} className="rd-btn rd-btn-secondary-light h-[52px] px-7 w-full sm:w-auto" data-testid="bottom-cta-find-deals">
              Find Deals
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
